import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, X } from "lucide-react";
import { useState } from "react";
import { useReserve } from "@/contexts/ReserveContext";
import { useFormattedPhone } from "@/lib/usePhone";
import { submitReserveOrder } from "@/lib/api";
import SmsConsent from "@/components/SmsConsent";

export default function Reserve() {
  const { reserve, removeFromReserve, clearReserve } = useReserve();
  const [secured, setSecured] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", sms_opt_in: false });
  const phone = useFormattedPhone();

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const subtotal = reserve.reduce((sum, it) => sum + Number(it.price || 0), 0);
  const phoneEntered = phone.hasValue;

  const complete = async (e) => {
    e?.preventDefault?.();
    if (!reserve.length) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required to secure your Reserve.");
      return;
    }
    if (phoneEntered && !phone.isValid) {
      toast.error(phone.error);
      return;
    }
    if (phoneEntered && !form.sms_opt_in) {
      toast.error("Please check the SMS consent box to receive texts.");
      return;
    }
    setSubmitting(true);
    try {
      await submitReserveOrder({
        name: form.name,
        email: form.email,
        phone: phone.normalized || undefined,
        sms_opt_in: !!form.sms_opt_in,
        items: reserve.map((it) => ({
          id: it.id, name: it.name, price: String(it.price), chapter_title: it.chapterTitle,
        })),
        subtotal: String(subtotal),
      });
      setSecured(true);
      clearReserve();
      toast.success("Your note has been delivered to The House. We'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not secure your Reserve.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (secured) {
    return (
      <div data-testid="reserve-page" className="pt-28 md:pt-36 pb-32 md:pb-40">
        <section className="mx-auto max-w-2xl px-6 md:px-10 text-center">
          <p className="label-eyebrow text-brass">Confirmed</p>
          <h1 className="font-display text-bone text-5xl md:text-7xl mt-6 leading-[0.95]">
            Your Reserve has been<br /><em className="not-italic text-brass">secured.</em>
          </h1>
          <p className="mt-8 text-bone/70 text-lg leading-relaxed">We&apos;ll have it ready for you soon.</p>
          <div className="mt-12 hairline mx-auto" />
          <Link to="/menu" data-testid="reserve-back-menu"
            className="mt-10 inline-flex items-center px-7 py-4 border border-bone/30 text-bone text-[11px] uppercase tracking-[0.3em] hover:bg-brass hover:text-obsidian hover:border-brass transition-colors">
            Continue Exploring The House
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="reserve-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1100px] px-6 md:px-10">
        <p className="label-eyebrow">The Reserve</p>
        <h1 className="font-display text-bone text-5xl md:text-7xl mt-5 leading-[0.95]">
          The <em className="not-italic text-brass">Reserve.</em>
        </h1>
        <p className="font-display italic text-bone/75 text-xl md:text-2xl mt-5 max-w-xl">
          A collection of your current obsessions.
        </p>

        {reserve.length === 0 ? <EmptyReserve /> : (
          <>
            <ul data-testid="reserve-list" className="mt-14 divide-y divide-bone/10 border-y border-bone/10">
              {reserve.map((item, idx) => (
                <li key={item.id} data-testid={`reserve-item-${item.id}`}
                  className="py-7 grid grid-cols-[auto_1fr_auto_auto] items-baseline gap-x-6 gap-y-2">
                  <Star size={14} className="text-brass mt-1" />
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-display text-bone text-2xl md:text-3xl">{item.name}</span>
                      <span className="text-bone/35 text-[10px] uppercase tracking-[0.28em]">
                        {item.chapterTitle} · N° {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    {item.desc && <p className="mt-2 text-bone/60 text-sm md:text-base leading-relaxed max-w-xl">{item.desc}</p>}
                  </div>
                  <span className="font-display text-brass text-2xl md:text-3xl">${item.price}</span>
                  <button type="button" data-testid={`reserve-remove-${item.id}`}
                    onClick={() => removeFromReserve(item.id)}
                    className="text-bone/45 hover:text-bone p-2 -m-2 transition-colors"
                    aria-label="Remove" title="Remove from The Reserve"><X size={16} /></button>
                </li>
              ))}
            </ul>

            <form onSubmit={complete} className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-7">
              <div className="md:col-span-2">
                <p className="label-eyebrow">Reserve Holder</p>
                <p className="text-bone/55 text-sm mt-3 max-w-md">
                  Tell us who&apos;s holding the Reserve. We confirm every order by hand.
                </p>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Name</label>
                <input data-testid="reserve-input-name" name="name" value={form.name} onChange={onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                  placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Email</label>
                <input data-testid="reserve-input-email" name="email" type="email" value={form.email} onChange={onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                  placeholder="you@somewhere.house" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">
                  Mobile Number <span className="text-bone/35">(optional)</span>
                </label>
                <input data-testid="reserve-input-phone" name="phone" type="tel" inputMode="tel"
                  value={phone.display} onChange={phone.onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                  placeholder="1 (555) 555-5555" />
                {phoneEntered && !phone.isValid && (
                  <p className="mt-2 text-[11px] text-red-300/85">{phone.error}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <SmsConsent testId="reserve-sms-consent" checked={form.sms_opt_in}
                  onChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))} required={phoneEntered} />
              </div>

              <div className="md:col-span-2 mt-2 grid md:grid-cols-2 gap-8 items-end">
                <div>
                  <p className="label-eyebrow">Curated Selection</p>
                  <p className="text-bone/55 text-sm mt-3 max-w-md">
                    Your Reserve is held by the House. Prices in USD. Final fulfilment is confirmed by hand.
                  </p>
                </div>
                <div className="md:text-right">
                  <p className="label-eyebrow text-bone/55">Subtotal</p>
                  <p className="font-display text-bone text-4xl md:text-5xl mt-2">${subtotal}</p>
                  <button type="submit" data-testid="reserve-complete" disabled={submitting}
                    className="mt-6 inline-flex items-center px-8 py-4 bg-brass text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-champagne transition-colors disabled:opacity-60">
                    {submitting ? "Securing…" : "Complete My Reserve"}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

function EmptyReserve() {
  return (
    <div data-testid="reserve-empty" className="mt-16 border border-bone/10 px-8 py-16 md:py-24 text-center">
      <p className="label-eyebrow">An Empty Shelf</p>
      <h2 className="font-display text-bone text-3xl md:text-5xl mt-5 leading-tight">The Reserve is empty.</h2>
      <p className="mt-6 font-display italic text-bone/70 text-lg md:text-xl max-w-xl mx-auto">
        Obsession is usually found when you&apos;re not looking for it.
      </p>
      <p className="mt-3 text-bone/60 max-w-md mx-auto">Go find something worth keeping.</p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link to="/menu" data-testid="reserve-empty-cta-menu"
          className="inline-flex items-center px-6 py-3 bg-lavender text-obsidian text-[11px] uppercase tracking-[0.3em] hover:bg-bone transition-colors">
          Open The Menu
        </Link>
        <Link to="/vault" data-testid="reserve-empty-cta-vault"
          className="inline-flex items-center px-6 py-3 border border-bone/30 text-bone text-[11px] uppercase tracking-[0.3em] hover:border-lavender hover:text-lavender transition-colors">
          Turn The Vault Wheel
        </Link>
      </div>
    </div>
  );
}
